/**
 * PEDIT-TREE — the generic node model + FK-linking builder for the project
 * entity tree (Project → Contract → Contract Position → Inspection Instruction
 * → Data Acquisition).
 *
 * The tree is modelled as generic `{ kind, id, code, name, state, children }`
 * nodes so future kinds (Reports, Issues, Invoices, …) slot in additively
 * (story 51 "extensible by design") — the renderer + the per-kind metadata
 * table are the only things that grow, not the link algorithm.
 *
 * Linking is purely client-side off the parent FKs the held BE branch serves:
 *   - contract.projectId          → groups under the project
 *   - contractPosition.contractId → links a position under its contract
 *   - inspectionInstruction.contractPositionId → links an II under its position
 *   - dataAcquisition.inspectionInstructionId  → links a DA under its II
 * A child whose FK doesn't resolve to a loaded parent is collected under a
 * synthetic "Unlinked" bucket at the appropriate level so nothing is dropped.
 */

export type EntityKind =
  | "project"
  | "contract"
  | "contractPosition"
  | "inspectionInstruction"
  | "dataAcquisition";

/** A generic tree node. `state` is null for the project root (no state chip)
 *  and for synthetic buckets. `code` is the human handle (project/contract
 *  code, position identifier, II code, DA code); `id` is the UUID. */
export interface EntityNode {
  kind: EntityKind;
  id: string;
  /** Display handle (e.g. "Contract 12", "WTG-01", "II 7"). */
  label: string;
  /** Raw code/identifier (shown in the context menu alongside the UUID). */
  code: string;
  /** UUID — hidden by default, revealed via the context menu. Null for
   *  synthetic buckets (no real entity behind them). */
  uuid: string | null;
  state: string | null;
  /** Direct children, already linked + sorted. */
  children: EntityNode[];
  /** A synthetic "Unlinked"/grouping node carries no real entity ⇒ no
   *  context-menu navigation. */
  synthetic?: boolean;
}

/* ── source rows (the linked-by-FK inputs) ───────────────────────────── */

export interface ContractRow {
  contractId: string;
  contractCode: number;
  projectId: string;
  state: string;
}
export interface PositionRow {
  contractPositionId: string;
  positionCode: number;
  assetDisplayLabel: string;
  contractId?: string | null;
  state: string;
}
export interface IiRow {
  inspectionInstructionId: string;
  instructionCode: number;
  contractPositionId: string;
  state: string;
}
export interface DaRow {
  id: string;
  instructionCode: number;
  inspectionInstructionId?: string | null;
  state: string;
}

export interface ProjectRoot {
  projectId: string;
  projectCode: number | string;
  projectName: string;
}

const UNLINKED = "__unlinked__";

function syntheticBucket(
  kind: EntityKind,
  label: string,
  children: EntityNode[],
): EntityNode {
  return {
    kind,
    id: `${UNLINKED}:${kind}`,
    label,
    code: "",
    uuid: null,
    state: null,
    children,
    synthetic: true,
  };
}

/**
 * Build the linked tree. Children are linked to their parent by FK; orphans
 * (FK absent or pointing at a parent not in the loaded set) are gathered under
 * a synthetic "Unlinked …" bucket so the PM still sees them.
 */
export function buildEntityTree(input: {
  project: ProjectRoot;
  contracts: ContractRow[];
  positions: PositionRow[];
  iis: IiRow[];
  das: DaRow[];
}): EntityNode {
  const { project, contracts, positions, iis, das } = input;

  // DAs by their parent II.
  const daByIi = new Map<string, EntityNode[]>();
  const orphanDas: EntityNode[] = [];
  for (const d of das) {
    const node: EntityNode = {
      kind: "dataAcquisition",
      id: d.id,
      label: `DA ${String(d.instructionCode)}`,
      code: String(d.instructionCode),
      uuid: d.id,
      state: d.state,
      children: [],
    };
    const parent = d.inspectionInstructionId;
    if (parent) {
      const bucket = daByIi.get(parent) ?? [];
      bucket.push(node);
      daByIi.set(parent, bucket);
    } else {
      orphanDas.push(node);
    }
  }

  // IIs by their parent position, each carrying its DAs.
  const iiByPosition = new Map<string, EntityNode[]>();
  const orphanIis: EntityNode[] = [];
  for (const ii of iis) {
    const daChildren = (daByIi.get(ii.inspectionInstructionId) ?? []).sort(
      byCodeNumeric,
    );
    const node: EntityNode = {
      kind: "inspectionInstruction",
      id: ii.inspectionInstructionId,
      label: `II ${String(ii.instructionCode)}`,
      code: String(ii.instructionCode),
      uuid: ii.inspectionInstructionId,
      state: ii.state,
      children: daChildren,
    };
    const parent = ii.contractPositionId;
    if (parent) {
      const bucket = iiByPosition.get(parent) ?? [];
      bucket.push(node);
      iiByPosition.set(parent, bucket);
    } else {
      orphanIis.push(node);
    }
  }

  // Positions by their parent contract, each carrying its IIs.
  const positionsByContract = new Map<string, EntityNode[]>();
  const orphanPositions: EntityNode[] = [];
  for (const p of positions) {
    const iiChildren = (iiByPosition.get(p.contractPositionId) ?? []).sort(
      byCodeNumeric,
    );
    const node: EntityNode = {
      kind: "contractPosition",
      id: p.contractPositionId,
      label: p.assetDisplayLabel || `Position ${String(p.positionCode)}`,
      code: p.assetDisplayLabel || String(p.positionCode),
      uuid: p.contractPositionId,
      state: p.state,
      children: iiChildren,
    };
    const parent = p.contractId ?? null;
    if (parent) {
      const bucket = positionsByContract.get(parent) ?? [];
      bucket.push(node);
      positionsByContract.set(parent, bucket);
    } else {
      orphanPositions.push(node);
    }
  }

  // Contracts under the project.
  const contractIds = new Set(contracts.map((c) => c.contractId));
  // Positions whose contractId points at a contract NOT in the loaded set are
  // orphans too (the FK is present but unresolvable) — route them to the bucket
  // so nothing is silently dropped.
  for (const [cid, posNodes] of positionsByContract) {
    if (!contractIds.has(cid)) orphanPositions.push(...posNodes);
  }
  const contractNodes: EntityNode[] = contracts
    .map((c) => {
      const posChildren = (positionsByContract.get(c.contractId) ?? []).sort(
        byLabel,
      );
      return {
        kind: "contract" as const,
        id: c.contractId,
        label: `Contract ${String(c.contractCode)}`,
        code: String(c.contractCode),
        uuid: c.contractId,
        state: c.state,
        children: posChildren,
      };
    })
    .sort(byCodeNumeric);

  // Attach the synthetic orphan buckets where they belong.
  if (orphanPositions.length > 0) {
    contractNodes.push(
      syntheticBucket(
        "contract",
        "Unlinked positions",
        orphanPositions.sort(byLabel),
      ),
    );
  }
  if (orphanIis.length > 0) {
    // IIs without a resolvable position → a position-level bucket at the top.
    contractNodes.push(
      syntheticBucket("contract", "Unlinked inspection instructions", [
        syntheticBucket(
          "contractPosition",
          "Inspection instructions",
          orphanIis.sort(byCodeNumeric),
        ),
      ]),
    );
  }
  if (orphanDas.length > 0) {
    contractNodes.push(
      syntheticBucket("contract", "Unlinked data acquisitions", [
        syntheticBucket(
          "contractPosition",
          "Data acquisitions",
          orphanDas.sort(byCodeNumeric),
        ),
      ]),
    );
  }

  return {
    kind: "project",
    id: project.projectId,
    label: `${project.projectName} (${String(project.projectCode)})`,
    code: String(project.projectCode),
    uuid: project.projectId,
    state: null,
    children: contractNodes,
  };
}

/* ── descendant counts (for the per-node "· N positions" summary) ────── */

/** Count direct + transitive children of a given kind under a node. */
export function countDescendants(node: EntityNode, kind: EntityKind): number {
  let n = 0;
  for (const c of node.children) {
    if (c.kind === kind && !c.synthetic) n += 1;
    n += countDescendants(c, kind);
  }
  return n;
}

/** A compact per-node count summary, e.g. "12 positions · 9 IIs · 40 DAs".
 *  Only the counts that are > 0 are shown; empty for leaves. */
export function countSummary(node: EntityNode): string {
  const parts: string[] = [];
  const pos = countDescendants(node, "contractPosition");
  const ii = countDescendants(node, "inspectionInstruction");
  const da = countDescendants(node, "dataAcquisition");
  if (node.kind === "project" || node.kind === "contract") {
    if (pos > 0)
      parts.push(`${String(pos)} ${pos === 1 ? "position" : "positions"}`);
  }
  if (
    node.kind === "project" ||
    node.kind === "contract" ||
    node.kind === "contractPosition"
  ) {
    if (ii > 0) parts.push(`${String(ii)} ${ii === 1 ? "II" : "IIs"}`);
  }
  if (da > 0) parts.push(`${String(da)} ${da === 1 ? "DA" : "DAs"}`);
  return parts.join(" · ");
}

/* ── per-kind presentation metadata ──────────────────────────────────── */

export interface KindMeta {
  /** Singular human noun for the context-menu "Go to <noun>" action. */
  noun: string;
  /**
   * The existing route to navigate to for "Go to". The project has a real
   * detail route (`/projects/:id`); the other kinds have only LIST routes
   * today (no per-entity detail page exists yet — see the FE router), so
   * "Go to" lands on the kind's list page. A `null` route ⇒ the "Go to"
   * action is omitted for that kind.
   */
  route: ((id: string) => string) | null;
}

export const KIND_META: Record<EntityKind, KindMeta> = {
  project: { noun: "project", route: (id) => `/projects/${id}` },
  // No contract/position/II/DA detail routes exist yet — land on the list page
  // (the existing route) so the PM can find the record.
  contract: { noun: "contract", route: () => `/contracts` },
  contractPosition: {
    noun: "contract position",
    route: () => `/contract-positions`,
  },
  inspectionInstruction: {
    noun: "inspection instruction",
    route: () => `/inspection-instructions`,
  },
  dataAcquisition: {
    noun: "data acquisition",
    route: () => `/data-acquisitions`,
  },
};

/* ── sorters ─────────────────────────────────────────────────────────── */

function byCodeNumeric(a: EntityNode, b: EntityNode): number {
  const na = Number(a.code);
  const nb = Number(b.code);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return a.label.localeCompare(b.label);
}
function byLabel(a: EntityNode, b: EntityNode): number {
  return a.label.localeCompare(b.label);
}
